
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useQuiz } from '@/hooks/useQuiz';
import QuizTimer from './quiz/QuizTimer';
import QuizProgress from './quiz/QuizProgress';
import QuizQuestion from './quiz/QuizQuestion';
import QuizNavigation from './quiz/QuizNavigation';
import QuizCompleted from './quiz/QuizCompleted';
import NoQuestionsAvailable from './quiz/NoQuestionsAvailable';

interface SkillQuizProps {
  skill: string;
  onComplete: () => void;
}

const SkillQuiz: React.FC<SkillQuizProps> = ({ skill, onComplete }) => {
  const { user } = useAuth();
  
  const {
    questions,
    currentQuestionIndex,
    selectedAnswers,
    timeLeft,
    totalTime,
    isLoading,
    isSubmitting,
    quizCompleted,
    score,
    passed,
    handleAnswerSelect,
    handleNextQuestion,
    handlePrevQuestion,
    handleSubmitQuiz,
    handleQuizEnd
  } = useQuiz(skill, user.id, onComplete);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="text-gray-800 text-xl">Loading questions...</div>
      </div>
    );
  }

  if (questions.length === 0) {
    return <NoQuestionsAvailable skill={skill} onComplete={onComplete} />;
  }

  if (quizCompleted) {
    return <QuizCompleted skill={skill} score={score} passed={passed} onComplete={onComplete} />;
  }

  const currentQuestion = questions[currentQuestionIndex];
  const answeredCount = Object.keys(selectedAnswers).length;

  return (
    <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>{skill} Quiz</CardTitle>
        </div>
        
        <QuizProgress 
          currentQuestionIndex={currentQuestionIndex}
          totalQuestions={questions.length}
          answeredCount={answeredCount}
          difficulty={currentQuestion.difficulty}
        />
        
        <QuizTimer 
          timeLeft={timeLeft} 
          totalTime={totalTime}
          onTimeUp={handleQuizEnd}
        />
      </CardHeader>
      
      <CardContent className="space-y-6">
        <QuizQuestion 
          question={currentQuestion}
          selectedAnswer={selectedAnswers[currentQuestion.id] || ''}
          onAnswerSelect={handleAnswerSelect}
        />
        
        <QuizNavigation 
          currentQuestionIndex={currentQuestionIndex}
          totalQuestions={questions.length}
          onPrevQuestion={handlePrevQuestion}
          onNextQuestion={handleNextQuestion}
          onSubmitQuiz={handleSubmitQuiz}
          isSubmitting={isSubmitting}
        />
      </CardContent>
    </Card>
  );
};

export default SkillQuiz;
